import { acquireApiToken } from "../auth/msalConfig"

const API_BASE = import.meta.env.VITE_API_URL || ""

export class ApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(`HTTP ${status}: ${detail}`)
  }
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await acquireApiToken()
  const headers = new Headers(init.headers)
  headers.set("Authorization", `Bearer ${token}`)
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json")

  const r = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (!r.ok) {
    let detail: string
    try {
      const j = await r.json()
      detail = j.detail || JSON.stringify(j)
    } catch {
      detail = await r.text()
    }
    throw new ApiError(r.status, detail)
  }
  return r.status === 204 ? (undefined as T) : r.json()
}

// Typed endpoints — kept intentionally thin in v0.1
export type Me = { oid: string; upn: string | null; name: string | null; roles: string[] }
export const getMe = () => api<Me>("/api/me")

export type AuditEntry = {
  id: string
  actor: string
  action: string
  target_type: string
  target_id: string
  created_at: string
}
export const writeAudit = (entry: Omit<AuditEntry, "id" | "actor" | "created_at"> & {
  before_state?: string | null
  after_state?: string | null
  trace_id?: string | null
}) => api<AuditEntry>("/api/audit", { method: "POST", body: JSON.stringify(entry) })

export const listAudit = (limit = 50) =>
  api<AuditEntry[]>(`/api/audit?limit=${limit}`)

// --- Plugin: launchpad -------------------------------------------------------
export type StudyStage =
  | "preflight" | "countdown" | "ignition" | "orbit" | "reentry" | "complete" | "aborted"

export type StudyListItem = {
  id: string
  project_code: string | null
  sponsor: string
  phase: string
  indication: string
  stage: StudyStage
  stage_clinical: string
  sites_active: number
  sites_total: number
  risk_level: "low" | "medium" | "high"
  pm_email: string | null
  pm_name: string | null
  planner_url: string | null
}

export type Study = StudyListItem & {
  fpi: string | null
  lplv_planned: string | null
  risk_signals_24h: number
  pm_oid: string | null
  planner_url: string | null
  planner_id: string | null
  bd_notes: string | null
  created_by_oid: string | null
  created_at: string | null
  updated_at: string | null
}

export type LaunchPadSummary = {
  as_of: string
  studies_total: number
  studies_in_orbit: number
  risk_signals_24h: number
  by_stage: Record<string, number>
}

export type StudyCreateInput = {
  id: string
  project_code?: string | null
  sponsor: string
  phase: "I" | "II" | "III" | "IV"
  indication: string
  sites_total?: number
  sites_active?: number
  pm_oid?: string | null
  pm_email?: string | null
  pm_name?: string | null
  planner_url?: string | null
  planner_id?: string | null
  bd_notes?: string | null
}

export type StudyUpdateInput = Partial<{
  project_code: string | null
  sponsor: string
  phase: "I" | "II" | "III" | "IV"
  indication: string
  sites_total: number
  sites_active: number
  risk_signals_24h: number
  risk_level: "low" | "medium" | "high"
  pm_oid: string | null
  pm_email: string | null
  pm_name: string | null
  planner_url: string | null
  planner_id: string | null
  bd_notes: string | null
}>

export type StageMoveInput = { to: StudyStage; reason?: string | null }

export type StageStat = { stage: string; stage_clinical: string; count: number }
export type StudyKPIs = {
  as_of: string
  studies_total: number
  studies_in_orbit: number
  risk_signals_24h: number
  sites_total: number
  sites_active: number
  site_activation_pct: number
  by_stage: StageStat[]
}

export const listStudies   = ()                  => api<StudyListItem[]>("/api/plugins/launchpad/studies")
export const getStudy      = (id: string)        => api<Study>(`/api/plugins/launchpad/studies/${id}`)
export const launchSummary = ()                  => api<LaunchPadSummary>("/api/plugins/launchpad/summary")

export const createStudy = (input: StudyCreateInput) =>
  api<Study>("/api/plugins/launchpad/studies", {
    method: "POST",
    body: JSON.stringify(input),
  })

export const updateStudy = (id: string, input: StudyUpdateInput) =>
  api<Study>(`/api/plugins/launchpad/studies/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  })

export const moveStudyStage = (id: string, input: StageMoveInput) =>
  api<Study>(`/api/plugins/launchpad/studies/${encodeURIComponent(id)}/stage`, {
    method: "PATCH",
    body: JSON.stringify(input),
  })

export const launchKpis = () => api<StudyKPIs>("/api/plugins/launchpad/kpis")

export const deleteStudy = (id: string) =>
  api<void>(`/api/plugins/launchpad/studies/${encodeURIComponent(id)}`, {
    method: "DELETE",
  })

export type LoadedPlugin = {
  id: string
  name: string
  version: string
  owner: string
  roles_required: string[]
}
export const listLoadedPlugins = () => api<LoadedPlugin[]>("/api/plugins")

// --- Plugin: bd (Business Development) ---------------------------------------
export type BdStageLocal =
  | "Qualification" | "Qualified and Nurturing" | "Proposed" | "Closed" | "Lost" | "Unmapped"

export type BdDeal = {
  id: string
  hubspot_deal_id: string
  deal_name: string
  stage_hubspot: string
  stage_local: BdStageLocal
  pipeline_hubspot: string | null
  amount: number | null
  amount_currency: string | null
  amount_usd: number | null
  amount_eur: number | null
  deal_probability: number | null
  owner_email: string | null
  owner_name: string | null
  company_name: string | null
  industry: string | null
  is_closed: boolean
  is_won: boolean
  is_lost: boolean
  created_at_hubspot: string | null
  modified_at_hubspot: string | null
  close_date: string | null
  last_synced_at: string | null
  // v0.7.1 — Mission Control DB row insert timestamp + a server-computed
  // flag that's true while the row is inside the rolling NEW window
  // (currently 7 days). Lets the dashboard show a "NEW" pill on deals
  // that appeared on recent syncs without per-user state.
  created_at: string | null
  is_new: boolean
}

export type BdSyncResult = {
  ok: boolean
  fetched: number
  upserted_new: number
  upserted_existing: number
  skipped: number
  elapsed_ms: number
  errors: string[]
  message: string
}

export type BdHubSpotTestResult = {
  ok: boolean
  category: string | null
  error: string | null
  latency_ms: number
  portal_id: string | null
  sample_deal: string | null
  total_deals_visible: number | null
}

export const listBdDeals = (filters: {
  stage_local?: BdStageLocal
  is_won?: boolean
  is_lost?: boolean
  include_unmapped?: boolean
} = {}) => {
  const q = new URLSearchParams()
  if (filters.stage_local) q.set("stage_local", filters.stage_local)
  if (filters.is_won !== undefined) q.set("is_won", String(filters.is_won))
  if (filters.is_lost !== undefined) q.set("is_lost", String(filters.is_lost))
  if (filters.include_unmapped) q.set("include_unmapped", "true")
  const qs = q.toString()
  return api<BdDeal[]>(`/api/plugins/bd/deals${qs ? `?${qs}` : ""}`)
}

// --- Quarterly KPIs (v0.6b) --------------------------------------------------
export type BdQuarterKpi = {
  label: string
  year: number
  quarter: number
  start_date: string
  end_date: string
  total_amount: number;             total_amount_pct_change: number | null
  weighted_amount: number;          weighted_amount_pct_change: number | null
  avg_per_deal: number;             avg_per_deal_pct_change: number | null
  open_amount: number;              open_amount_pct_change: number | null
  closed_amount: number;            closed_amount_pct_change: number | null
  lost_amount: number;              lost_amount_pct_change: number | null
  new_amount: number;               new_amount_pct_change: number | null
  deals_total: number
}

export type BdKpisOut = {
  as_of: string
  currency_basis: "EUR" | "USD"
  usd_per_eur: number
  quarters: BdQuarterKpi[]
}

export const getBdKpis = (currency: "EUR" | "USD" = "EUR") =>
  api<BdKpisOut>(`/api/plugins/bd/kpis?currency=${currency}`)

// --- Sales projection (v0.7.2) ----------------------------------------------
// Server computes YTD closed-won + weighted expected pipeline for the
// current calendar year. The frontend just renders.
export type BdProjection = {
  year: number
  as_of: string
  currency_basis: "EUR" | "USD"
  usd_per_eur: number
  ytd_closed_won_amount: number
  ytd_closed_won_count: number
  ytd_closed_lost_amount: number
  ytd_closed_lost_count: number
  expected_from_pipeline_dated: number
  expected_from_pipeline_undated: number
  expected_from_pipeline_total: number
  projection_total: number
  historical_win_rate_pct: number | null
  win_rate_window_label: string
  open_deals_with_close_date_this_year: number
  open_deals_without_close_date: number
}

export const getBdProjection = (currency: "EUR" | "USD" = "EUR") =>
  api<BdProjection>(`/api/plugins/bd/projection?currency=${currency}`)

// --- Insights (v0.6b) --------------------------------------------------------
export type BdStageBucket   = { stage_local: string; deal_count: number; amount: number }
export type BdStaleDeal     = {
  id: string; hubspot_deal_id: string; deal_name: string
  stage_local: string; amount: number; company_name: string | null
  owner_email: string | null; last_modified: string | null; days_since_modified: number
}
export type BdTopDeal       = {
  id: string; hubspot_deal_id: string; deal_name: string
  stage_local: string; amount: number; company_name: string | null; industry: string | null
}
export type BdInsightsOut = {
  as_of: string
  currency_basis: "EUR" | "USD"
  stage_waterfall: BdStageBucket[]
  stale_deals: BdStaleDeal[]
  stale_threshold_days: number
  top_open_deals: BdTopDeal[]
}

export const getBdInsights = (currency: "EUR" | "USD" = "EUR", stale_threshold_days = 21, top_n = 5) =>
  api<BdInsightsOut>(
    `/api/plugins/bd/insights?currency=${currency}&stale_threshold_days=${stale_threshold_days}&top_n=${top_n}`,
  )

export const syncBdFromHubSpot = () =>
  api<BdSyncResult>("/api/plugins/bd/sync", { method: "POST" })

export const testHubSpotConnection = () =>
  api<BdHubSpotTestResult>("/api/plugins/bd/hubspot/test", { method: "POST" })

// --- BD handover (v0.6c) -----------------------------------------------------
// Lifecycle: POST /handover/draft → PATCH /handover (saves) → POST /handover/send
// (creates pmo.studies). GET /handover returns whatever exists, including the
// 'sent' record for a deal that's already been handed over (so the UI can show
// the converted-study pill instead of the draft modal).

export type BdHandoverStatus = "draft" | "sent"

export type BdHandover = {
  id: string
  deal_id: string
  status: BdHandoverStatus
  subject: string | null
  body_markdown: string
  recipient: string | null
  sharepoint_url: string | null
  planned_study_id:   string | null
  planned_sponsor:    string | null
  planned_phase:      string | null
  planned_indication: string | null
  generated_by_model: string | null
  generated_at: string | null
  last_edited_by_upn: string | null
  last_edited_at: string | null
  sent_at: string | null
  sent_to: string | null
  converted_to_study_id: string | null
  // v0.7.3 — handover document workflow
  has_sow_pdf: boolean
  sow_pdf_filename: string | null
  has_document: boolean
  document_filename: string | null
  document_generated_at: string | null
}

export type BdHandoverDocStatus = {
  has_sow_pdf: boolean
  sow_pdf_filename: string | null
  has_document: boolean
  document_filename: string | null
  document_generated_at: string | null
  extracted_fields: Record<string, unknown> | null
}

export type BdHandoverPatch = {
  subject?: string
  body_markdown?: string
  recipient?: string
  sharepoint_url?: string
  planned_study_id?: string
  planned_sponsor?: string
  planned_phase?: "I" | "II" | "III" | "IV"
  planned_indication?: string
}

export type BdHandoverSendIn = {
  subject: string
  body_markdown: string
  recipient?: string
  sharepoint_url?: string
  study_id: string
  sponsor: string
  phase: "I" | "II" | "III" | "IV"
  indication: string
}

export type BdHandoverSendOut = {
  ok: boolean
  handover_id: string
  study_id: string
  sent_to: string
  sent_at: string
  email_latency_ms: number
  message: string
}

/** Generate (or, with refresh, regenerate) the AI handover draft. */
export const draftBdHandover = (dealId: string, refresh = false) =>
  api<BdHandover>(
    `/api/plugins/bd/deals/${dealId}/handover/draft${refresh ? "?refresh=true" : ""}`,
    { method: "POST" },
  )

/** Returns 404 if no draft exists for the deal yet. */
export const getBdHandover = (dealId: string) =>
  api<BdHandover>(`/api/plugins/bd/deals/${dealId}/handover`)

/** Save edits without sending. */
export const patchBdHandover = (dealId: string, patch: BdHandoverPatch) =>
  api<BdHandover>(`/api/plugins/bd/deals/${dealId}/handover`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  })

/** Send via Graph + create pmo.studies in one transaction. */
export const sendBdHandover = (dealId: string, payload: BdHandoverSendIn) =>
  api<BdHandoverSendOut>(`/api/plugins/bd/deals/${dealId}/handover/send`, {
    method: "POST",
    body: JSON.stringify(payload),
  })

// --- BD → Finance handover (v0.8.2) -----------------------------------------
export type BdFinanceHandoverOut = {
  ok: boolean
  contract_id: string
  already_existed: boolean
  used_sow_extraction: boolean
  customer_name: string
  message: string
}

/** Create a Finance contract from a BD deal (reuses the PMO-handover SOW
 *  for OpenAI extraction when present, else seeds from the deal). */
export const handoverDealToFinance = (dealId: string) =>
  api<BdFinanceHandoverOut>(`/api/plugins/bd/deals/${dealId}/handover/finance`, { method: "POST" })

// --- Handover document (v0.7.3) ---------------------------------------------
// Multipart form uploads + raw binary downloads need their own wrappers
// (the typed `api<T>` helper assumes JSON in/out). We still go through
// acquireApiToken() so auth headers match.

async function _multipartUpload<T>(
  path: string, method: "POST" | "PUT", file: File, fieldName = "file",
): Promise<T> {
  const token = await acquireApiToken()
  const fd = new FormData()
  fd.append(fieldName, file, file.name)
  const r = await fetch(`${API_BASE}${path}`, {
    method,
    body: fd,
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!r.ok) {
    let detail: string
    try {
      const j = await r.json()
      detail = j.detail || JSON.stringify(j)
    } catch {
      detail = await r.text()
    }
    throw new ApiError(r.status, detail)
  }
  return r.status === 204 ? (undefined as T) : r.json()
}

/** Upload signed SOW PDF; backend extracts via OpenAI + generates the .docx. */
export const uploadBdHandoverSow = (dealId: string, pdf: File) =>
  _multipartUpload<BdHandoverDocStatus>(
    `/api/plugins/bd/deals/${dealId}/handover/sow`, "POST", pdf,
  )

/** Replace the generated .docx with a BD-edited version. */
export const replaceBdHandoverDocument = (dealId: string, docx: File) =>
  _multipartUpload<BdHandoverDocStatus>(
    `/api/plugins/bd/deals/${dealId}/handover/document`, "PUT", docx,
  )

/** Trigger a browser download of the current handover .docx. Returns
 *  the suggested filename from Content-Disposition. */
export async function downloadBdHandoverDocument(
  dealId: string, fallbackFilename = "handover.docx",
): Promise<void> {
  const token = await acquireApiToken()
  const r = await fetch(`${API_BASE}/api/plugins/bd/deals/${dealId}/handover/document`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!r.ok) {
    let detail: string
    try { detail = (await r.json()).detail } catch { detail = await r.text() }
    throw new ApiError(r.status, detail)
  }
  // Parse Content-Disposition for the server-suggested filename.
  let filename = fallbackFilename
  const cd = r.headers.get("Content-Disposition") || ""
  const m = cd.match(/filename="?([^";]+)"?/)
  if (m && m[1]) filename = m[1]
  const blob = await r.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// --- Plugin: finance (v0.8) --------------------------------------------------
export type FinanceContract = {
  id: string
  customer_name: string
  customer_company_code: string | null
  customer_contact_name: string | null
  customer_contact_email: string | null
  customer_contact_phone: string | null
  ap_emails: string | null
  contract_ref: string | null
  title: string | null
  sponsor: string | null
  start_date: string | null
  end_date: string | null
  setup_fee: number | null
  license_fee: number | null
  license_months: number | null
  license_fee_followup: number | null
  license_followup_months: number | null
  current_monthly_fee: number | null
  current_fee_tier: string | null
  billed_per_period: number | null
  total_amount: number | null
  currency: string
  invoicing_unit: string | null
  payment_target_days: number | null
  po_number: string | null
  status: string
  source: string
  hubspot_deal_id: string | null
  has_source_pdf: boolean
  source_pdf_filename: string | null
  notes: string | null
  created_by_upn: string | null
  created_at: string | null
  updated_at: string | null
}

export type FinanceContractPage = {
  items: FinanceContract[]
  total: number
  page: number
  page_size: number
  total_pages: number
  sort: string
  order: "asc" | "desc"
  q: string | null
}

export type FinanceContractInput = {
  customer_name: string
  customer_company_code?: string | null
  customer_contact_name?: string | null
  customer_contact_email?: string | null
  customer_contact_phone?: string | null
  ap_emails?: string | null
  contract_ref?: string | null
  title?: string | null
  sponsor?: string | null
  start_date?: string | null
  end_date?: string | null
  setup_fee?: number | null
  license_fee?: number | null
  license_months?: number | null
  license_fee_followup?: number | null
  license_followup_months?: number | null
  total_amount?: number | null
  currency?: string
  invoicing_unit?: "month" | "quarter" | "year" | "user" | null
  payment_target_days?: number | null
  po_number?: string | null
  status?: "draft" | "active" | "completed" | "cancelled"
  hubspot_deal_id?: string | null
  notes?: string | null
}

export type FinanceListParams = {
  q?: string
  status?: string
  sort?: string
  order?: "asc" | "desc"
  page?: number
  page_size?: number
}

export const listFinanceContracts = (params: FinanceListParams = {}) => {
  const qs = new URLSearchParams()
  if (params.q) qs.set("q", params.q)
  if (params.status) qs.set("status", params.status)
  if (params.sort) qs.set("sort", params.sort)
  if (params.order) qs.set("order", params.order)
  if (params.page) qs.set("page", String(params.page))
  if (params.page_size) qs.set("page_size", String(params.page_size))
  const s = qs.toString()
  return api<FinanceContractPage>(`/api/plugins/finance/contracts${s ? "?" + s : ""}`)
}

export const getFinanceContract = (id: string) =>
  api<FinanceContract>(`/api/plugins/finance/contracts/${id}`)

export const createFinanceContract = (body: FinanceContractInput) =>
  api<FinanceContract>("/api/plugins/finance/contracts", {
    method: "POST", body: JSON.stringify(body),
  })

export const updateFinanceContract = (id: string, body: Partial<FinanceContractInput>) =>
  api<FinanceContract>(`/api/plugins/finance/contracts/${id}`, {
    method: "PATCH", body: JSON.stringify(body),
  })

export const deleteFinanceContract = (id: string) =>
  api<void>(`/api/plugins/finance/contracts/${id}`, { method: "DELETE" })

// Invoice side-panel (v0.8.3) — HubSpot invoices in the contract's context.
export type FinanceInvoiceRow = {
  id: string
  number: string | null
  status: string | null
  po_number: string | null
  po_matches_contract: boolean | null
  amount_billed: number | null
  balance_due: number | null
  currency: string | null
  invoice_date: string | null
  due_date: string | null
  estimated_payment_date: string | null
  is_overdue: boolean
  days_overdue: number | null
  deal_linked: boolean
}
export type FinanceInvoiceSummary = {
  contract_id: string
  matched_by: "deal_company" | "name_search" | "none"
  company_ids: string[]
  matched_company_name: string | null
  count_total: number
  count_paid: number
  count_open: number
  count_voided: number
  total_billed: number
  total_paid: number
  total_outstanding: number
  contract_total: number | null
  remaining_in_contract: number | null
  license_fee_monthly: number | null
  invoicing_unit: string | null
  expected_per_period: number | null
  avg_invoice_billed: number | null
  contract_po_number: string | null
  payment_target_days: number | null
  po_mismatch_count: number
  overdue_count: number
  overdue_amount: number
  ap_emails: string[]
  currency: string
  invoices: FinanceInvoiceRow[]
  note: string | null
}
export const getFinanceContractInvoices = (id: string) =>
  api<FinanceInvoiceSummary>(`/api/plugins/finance/contracts/${id}/invoices`)

// AR reminder (v0.9.0) — pre-generated overdue-invoice reminder email.
export type FinanceReminderPreviewIn = {
  invoice_number?: string | null
  invoice_date?: string | null
  due_date?: string | null
  days_overdue?: number | null
  amount_due?: number | null
  currency?: string
  po_number?: string | null
}
export type FinanceReminderPreview = {
  subject: string
  body: string
  suggested_recipients: string[]
  from_name: string
  contract_po_number: string | null
}
export type FinanceReminderSendOut = {
  ok: boolean
  sent_to: string[]
  message: string
  latency_ms: number
}
export const previewFinanceReminder = (contractId: string, body: FinanceReminderPreviewIn) =>
  api<FinanceReminderPreview>(`/api/plugins/finance/contracts/${contractId}/reminder/preview`, {
    method: "POST", body: JSON.stringify(body),
  })
export const sendFinanceReminder = (
  contractId: string,
  body: { to: string[]; subject: string; body: string; invoice_number?: string | null },
) =>
  api<FinanceReminderSendOut>(`/api/plugins/finance/contracts/${contractId}/reminder/send`, {
    method: "POST", body: JSON.stringify(body),
  })

// Corporate dashboard (v0.8.4) — Excel model + contract-derived metrics.
export type FinanceDashSeriesPoint = {
  month: string
  revenue: number | null
  expenses: number | null
  cash: number | null
  expense_budget: number | null
}
export type FinanceTopCustomer = {
  customer_name: string
  total_amount: number | null
  license_fee: number | null
  invoicing_unit: string | null
  currency: string
}
export type FinanceDashboard = {
  has_model: boolean
  model_label: string | null
  model_filename: string | null
  model_uploaded_at: string | null
  currency: string
  as_of_month: string | null
  latest_quarter: string | null
  cash: number | null
  gross_margin_pct: number | null
  revenue_latest_q: number | null
  net_profit_latest_q: number | null
  total_expenses_latest_q: number | null
  runway_months: number | null
  months_extracted: number
  series: FinanceDashSeriesPoint[]
  mrr: number
  arr: number
  active_contracts: number
  total_contract_value: number
  top_customers: FinanceTopCustomer[]
}

export const getFinanceDashboard = () =>
  api<FinanceDashboard>("/api/plugins/finance/dashboard")

// Enterprise Valuation (v0.9.1) — EV = ARR × multiple, SaaS Capital Index ref.
export type FinanceValuationEvPoint = {
  month: string
  arr: number
  ev_consensus: number
  ev_index: number
}
export type FinanceValuation = {
  currency: string
  usd_per_eur: number
  current_arr: number
  current_mrr: number
  consensus_multiple: number
  multiple_is_default: boolean
  ev_current: number
  saas_capital_current: number
  saas_capital_as_of: string
  saas_capital_source_url: string
  saas_capital_history: { period: string; multiple: number }[]
  ev_history: FinanceValuationEvPoint[]
  has_revenue_series: boolean
}
export const getFinanceValuation = () =>
  api<FinanceValuation>("/api/plugins/finance/valuation")
export const setFinanceValuationMultiple = (multiple: number) =>
  api<FinanceValuation>("/api/plugins/finance/valuation/multiple", {
    method: "PUT", body: JSON.stringify({ multiple }),
  })

/** Upload a new financial-model .xlsx; backend parses + recalcs. */
export async function uploadFinanceDashboardExcel(xlsx: File): Promise<FinanceDashboard> {
  const token = await acquireApiToken()
  const fd = new FormData()
  fd.append("file", xlsx, xlsx.name)
  const r = await fetch(`${API_BASE}/api/plugins/finance/dashboard/excel`, {
    method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
  })
  if (!r.ok) {
    let detail: string
    try { detail = (await r.json()).detail } catch { detail = await r.text() }
    throw new ApiError(r.status, detail)
  }
  return r.json()
}

/** AI import — upload a signed contract PDF; backend extracts the fields
 *  with OpenAI and returns a pre-filled DRAFT contract for review. */
export async function extractFinanceContract(pdf: File): Promise<FinanceContract> {
  const token = await acquireApiToken()
  const fd = new FormData()
  fd.append("file", pdf, pdf.name)
  const r = await fetch(`${API_BASE}/api/plugins/finance/contracts/extract`, {
    method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
  })
  if (!r.ok) {
    let detail: string
    try { detail = (await r.json()).detail } catch { detail = await r.text() }
    throw new ApiError(r.status, detail)
  }
  return r.json()
}

// --- Loyalty Shares (v0.10.0) ------------------------------------------------
// Virtual-share scheme for the German GmbH. A holder uploads their agreement,
// confirms the parameters, and sees the cash they'd receive if an Exit
// happened today (grant valued against the company Enterprise Value).
export type LoyaltyValuation = {
  status: string
  num_virtual_shares: number
  vested_shares: number
  vested_pct: number
  vesting_months_total: number | null
  virtual_strike_price: number
  enterprise_value: number
  total_company_shares: number
  total_incentive_pool: number
  per_share_equity_value: number
  benefit_per_share: number
  is_in_the_money: boolean
  payout_today: number
  payout_if_fully_vested: number
  currency: string
  usd_per_eur: number
}
export type LoyaltyGrant = {
  id: string
  beneficiary_name: string | null
  virtual_strike_price: number | null
  num_virtual_shares: number | null
  vesting_fraction: string | null
  fixed_period_months: number | null
  devesting_fraction: string | null
  exit_threshold: string | null
  start_date: string | null
  longstop_date: string | null
  termination_date: string | null
  currency: string
  has_source_pdf: boolean
  source_pdf_filename: string | null
  notes: string | null
  created_at: string | null
  updated_at: string | null
  valuation: LoyaltyValuation | null
}
export type LoyaltyGrantInput = {
  beneficiary_name?: string | null
  virtual_strike_price?: number | null
  num_virtual_shares?: number | null
  vesting_fraction?: string | null
  fixed_period_months?: number | null
  devesting_fraction?: string | null
  exit_threshold?: string | null
  start_date?: string | null
  longstop_date?: string | null
  termination_date?: string | null
  currency?: string
  notes?: string | null
}
export type LoyaltyInputs = {
  currency: string
  usd_per_eur: number
  enterprise_value: number
  arr: number
  consensus_multiple: number
  multiple_is_default: boolean
  total_company_shares: number
  total_incentive_pool: number
  per_share_equity_value: number
  valuation_as_of: string
  can_manage_inputs: boolean
}
export type LoyaltyExtract = { fields: LoyaltyGrantInput; model: string; filename: string | null }

export const getLoyaltyInputs = () => api<LoyaltyInputs>("/api/plugins/finance/loyalty/inputs")
export const setLoyaltyInputs = (total_company_shares: number, total_incentive_pool: number) =>
  api<LoyaltyInputs>("/api/plugins/finance/loyalty/inputs", {
    method: "PUT", body: JSON.stringify({ total_company_shares, total_incentive_pool }),
  })
export const listLoyaltyGrants = () => api<LoyaltyGrant[]>("/api/plugins/finance/loyalty/grants")
export const createLoyaltyGrant = (g: LoyaltyGrantInput) =>
  api<LoyaltyGrant>("/api/plugins/finance/loyalty/grants", { method: "POST", body: JSON.stringify(g) })
export const updateLoyaltyGrant = (id: string, g: LoyaltyGrantInput) =>
  api<LoyaltyGrant>(`/api/plugins/finance/loyalty/grants/${id}`, { method: "PATCH", body: JSON.stringify(g) })
export const deleteLoyaltyGrant = (id: string) =>
  api<void>(`/api/plugins/finance/loyalty/grants/${id}`, { method: "DELETE" })

/** Upload a Loyalty Share Agreement PDF; backend AI-extracts the parameters. */
export async function extractLoyaltyAgreement(pdf: File): Promise<LoyaltyExtract> {
  const token = await acquireApiToken()
  const fd = new FormData()
  fd.append("file", pdf, pdf.name)
  const r = await fetch(`${API_BASE}/api/plugins/finance/loyalty/extract`, {
    method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
  })
  if (!r.ok) {
    let detail: string
    try { detail = (await r.json()).detail } catch { detail = await r.text() }
    throw new ApiError(r.status, detail)
  }
  return r.json()
}

// --- Operations: CF monitoring (v0.11.0) -------------------------------------
export type OpsEnvironment = {
  id: string
  display_name: string
  kind: string
  customer_name: string | null
  base_url: string
  cfux_base: string
  has_token: boolean
  token_hint: string | null
  poll_interval_sec: number
  siid_scope: string | null
  enabled: boolean
  sort_order: number
  notes: string | null
  created_at: string | null
  updated_at: string | null
}
export type OpsEnvironmentInput = {
  id?: string
  display_name?: string
  kind?: string
  customer_name?: string | null
  base_url?: string
  token?: string | null
  poll_interval_sec?: number
  siid_scope?: string | null
  enabled?: boolean
  sort_order?: number
  notes?: string | null
}
export type OpsEnvTest = {
  env_id: string
  reachable: boolean
  cf_version: string | null
  git_hash: string | null
  token_present: boolean
  token_valid: boolean | null
  token_apis: string[] | null
  token_siids: string[] | null
  token_expiry: string | null
  message: string | null
}
export type OpsOverviewCard = {
  id: string
  display_name: string
  kind: string
  customer_name: string | null
  enabled: boolean
  cfux_base: string
  status: string
  reachable: boolean
  cf_version: string | null
  git_hash: string | null
  active_instances: number | null
  total_instances: number | null
  queue_pending: number | null
  studies_total: number | null
  studies_capped: boolean
  error: string | null
}
export type OpsOverview = { generated_at: string; environments: OpsOverviewCard[] }
export type OpsStudyRow = {
  siid: string | null
  internal_id: string | null
  af_type: string | null
  offline: boolean | null
  version_hash: string | null
  time_updated: string | null
  updated_calc_count: number | null
  last_result: string | null
}
export type OpsStudies = {
  env_id: string
  cf_version: string | null
  total: number | null
  truncated: boolean
  items: OpsStudyRow[]
  error: string | null
}

export const listOpsEnvironments = () => api<OpsEnvironment[]>("/api/plugins/ops/environments")
export const createOpsEnvironment = (e: OpsEnvironmentInput) =>
  api<OpsEnvironment>("/api/plugins/ops/environments", { method: "POST", body: JSON.stringify(e) })
export const updateOpsEnvironment = (id: string, e: OpsEnvironmentInput) =>
  api<OpsEnvironment>(`/api/plugins/ops/environments/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(e) })
export const deleteOpsEnvironment = (id: string) =>
  api<void>(`/api/plugins/ops/environments/${encodeURIComponent(id)}`, { method: "DELETE" })
export const testOpsEnvironment = (id: string) =>
  api<OpsEnvTest>(`/api/plugins/ops/environments/${encodeURIComponent(id)}/test`, { method: "POST" })
export type OpsStudyImport = {
  data_import_id: string | null
  workflow: string | null
  time_created: string | null
  time_processed: string | null
  processed: boolean | null
  value_time_start: string | null
  value_time_end: string | null
  issue_count: number
}
export type OpsStudyIssue = {
  data_import_id: string | null
  time_created: string | null
  fact_name: string | null
  field: string | null
  reason: string | null
  details: string | null
  origin: string | null
  count: number
  raw: string | null
}
export type OpsStudyCrGroup = {
  group_id: string | null
  batch_id: string | null
  workflow: string | null
  failed_crs: number
  failed_calcs: number
  failed_calcs_critical: number
  total_calcs: number
  has_failures: boolean
  time_created: string | null
  time_finished: string | null
  run_minutes: number | null
}
export type OpsStudyActivity = {
  env_id: string
  siid: string
  days: number
  since: string
  cf_version: string | null
  imports: OpsStudyImport[]
  issues: OpsStudyIssue[]
  total_issue_count: number
  cr_groups: OpsStudyCrGroup[]
  numeric_issue_count: number | null
  failed_group_count: number
  truncated: boolean
  errors: string[]
}
export const getOpsStudyActivity = (envId: string, siid: string, days = 10) =>
  api<OpsStudyActivity>(`/api/plugins/ops/environments/${encodeURIComponent(envId)}/studies/${encodeURIComponent(siid)}/activity?days=${days}`)

export const getOpsOverview = () => api<OpsOverview>("/api/plugins/ops/overview")
export const listOpsStudies = (envId: string, q?: string, type?: string, limit = 500) => {
  const p = new URLSearchParams()
  if (q) p.set("q", q)
  if (type) p.set("type", type)
  p.set("limit", String(limit))
  return api<OpsStudies>(`/api/plugins/ops/environments/${encodeURIComponent(envId)}/studies?${p.toString()}`)
}

// IT Type inventory + last-WF-result analytics (v0.12.0)
export type OpsStudyType = { type: string; count: number | null }
export type OpsStudyTypes = { env_id: string; types: OpsStudyType[]; scan_truncated: boolean; error: string | null }
export type OpsWfResult = {
  workflow: string; ok: boolean
  group_id: string | null; data_import_id: string | null
  time_finished: string | null; error: string | null; stage_count: number | null
}
export type OpsStudyLastResults = {
  siid: string; af_type: string | null; offline: boolean | null
  workflows: OpsWfResult[]; has_failures: boolean; enriched: boolean
}
export type OpsAnalyticsRollup = {
  total_studies: number; enriched: number; with_failures: number
  by_workflow: Record<string, { ok: number; failed: number }>
  by_type: Record<string, { studies: number; with_failures: number }>
}
export type OpsStudyAnalytics = {
  env_id: string; type: string | null; generated_at: string
  rollup: OpsAnalyticsRollup; studies: OpsStudyLastResults[]; truncated: boolean; errors: string[]
}
// Per-user favorites (server-persisted, survive login) + failure timeseries (v0.13.0)
export type OpsFavorite = { env_id: string; siid: string }
export const listOpsFavorites = () => api<OpsFavorite[]>("/api/plugins/ops/favorites")
export const setOpsFavorites = (env_id: string, siids: string[]) =>
  api<OpsFavorite[]>("/api/plugins/ops/favorites", { method: "PUT", body: JSON.stringify({ env_id, siids }) })

export type OpsFailurePoint = { bucket: string; workflow: string; failed: number; total: number }
export type OpsFailureTimeseries = {
  env_id: string; type: string | null; siids: string[]
  days: number; bucket: string; since: string
  points: OpsFailurePoint[]; workflows: string[]
  truncated: boolean; errors: string[]; generated_at: string
}
export const getOpsFailureTimeseries = (envId: string, opts: { type?: string; siids?: string[]; days?: number; bucket?: "day" | "week" }) => {
  const p = new URLSearchParams()
  if (opts.type) p.set("type", opts.type)
  if (opts.siids?.length) p.set("siids", opts.siids.join(","))
  p.set("days", String(opts.days ?? 30))
  p.set("bucket", opts.bucket ?? "day")
  return api<OpsFailureTimeseries>(`/api/plugins/ops/environments/${encodeURIComponent(envId)}/failure-timeseries?${p.toString()}`)
}

export const listOpsStudyTypes = (envId: string) =>
  api<OpsStudyTypes>(`/api/plugins/ops/environments/${encodeURIComponent(envId)}/study-types`)
export const getOpsStudyAnalytics = (envId: string, type?: string, q?: string) => {
  const p = new URLSearchParams()
  if (type) p.set("type", type)
  if (q) p.set("q", q)
  return api<OpsStudyAnalytics>(`/api/plugins/ops/environments/${encodeURIComponent(envId)}/study-analytics?${p.toString()}`)
}

// --- Admin: platform settings ------------------------------------------------
export type PlatformSetting = {
  key: string
  value: string
  description: string | null
  updated_at: string
}

export const listPlatformSettings = () => api<PlatformSetting[]>("/api/admin/settings")
export const getPlatformSetting   = (key: string) =>
  api<PlatformSetting>(`/api/admin/settings/${encodeURIComponent(key)}`)
export const patchPlatformSetting = (key: string, value: string, description?: string) =>
  api<PlatformSetting>(`/api/admin/settings/${encodeURIComponent(key)}`, {
    method: "PATCH",
    body: JSON.stringify({ value, ...(description !== undefined ? { description } : {}) }),
  })

// --- Admin: OpenAI test ------------------------------------------------------
export type OpenAITestResult = {
  ok: boolean
  category: "configuration" | "timeout" | "azure-openai" | "unknown" | null
  error: string | null
  latency_ms: number
  response_text: string | null
  model: string | null
  endpoint: string | null
  deployment: string | null
}
export const testOpenAI = () =>
  api<OpenAITestResult>("/api/admin/openai/test", { method: "POST" })

// --- Admin: Email send test (v0.6.6) ----------------------------------------
export type EmailTestResult = {
  ok: boolean
  category: "token" | "permission" | "configuration" | "network" | "graph-api" | null
  error: string | null
  latency_ms: number
  roles: string[]
  appid: string | null
  from_mailbox?: string | null
  message?: string
}
export const testEmail = (target?: string) => {
  const qs = target ? `?target=${encodeURIComponent(target)}` : ""
  return api<EmailTestResult>(`/api/admin/email/test${qs}`, { method: "POST" })
}

// --- Plugin: rbqm-trends -----------------------------------------------------
export type TrendCategory = "commercial" | "financial" | "regulatory" | "competitive"
export type SourceKind = "media" | "regulator" | "competitor"
export type TrendStatus = "new" | "monitored" | "stale"

export type RbqmSource = {
  id: string
  name: string
  kind: SourceKind
  home_url: string | null
  rss_url: string | null
  enabled: boolean
  last_polled_at: string | null
  poll_failure_count: number
  notes: string | null
}

export type RbqmTrend = {
  id: string
  category: TrendCategory
  title: string
  summary: string
  recommendations: string | null
  status: TrendStatus
  first_seen_at: string
  last_evaluated_at: string
  last_evaluation_model: string | null
}

export const listRbqmSources = (kind?: SourceKind) =>
  api<RbqmSource[]>(`/api/plugins/rbqm-trends/sources${kind ? `?kind=${kind}` : ""}`)

export const listRbqmTrends = (filters: {
  category?: TrendCategory
  status?: TrendStatus
  period?: "month" | "quarter" | "6months" | "year" | "all"
} = {}) => {
  const q = new URLSearchParams()
  if (filters.category) q.set("category", filters.category)
  if (filters.status) q.set("status_filter", filters.status)
  if (filters.period && filters.period !== "all") q.set("period", filters.period)
  const qs = q.toString()
  return api<RbqmTrend[]>(`/api/plugins/rbqm-trends/trends${qs ? `?${qs}` : ""}`)
}

export type RbqmPollResult = {
  source_id: string
  source_name: string
  ok: boolean
  fetched: number
  inserted: number
  error: string | null
  elapsed_ms: number
}
export type RbqmPollAllResult = {
  poll_started_at: string
  poll_finished_at: string
  sources_attempted: number
  sources_succeeded: number
  items_fetched: number
  items_inserted: number
  per_source: RbqmPollResult[]
}
export type RbqmExtractStats = {
  raw_items_considered: number
  existing_trends_context: number
  trends_emitted_by_ai: number
  trends_inserted: number
  trends_reconfirmed: number
  trends_marked_stale: number
  model_used: string | null
  elapsed_ms: number
  errors: string[]
}

export type RbqmRefreshResult = {
  ok: boolean
  stage: string
  message: string
  ships_in_next?: string
  ingestion?: RbqmPollAllResult
  extraction?: RbqmExtractStats
}

export const refreshRbqmTrends = () =>
  api<RbqmRefreshResult>("/api/plugins/rbqm-trends/refresh", { method: "POST" })

export const extractRbqmTrends = (opts: { days_window?: number; dry_run?: boolean } = {}) =>
  api<RbqmExtractStats>("/api/plugins/rbqm-trends/extract", {
    method: "POST",
    body: JSON.stringify(opts),
  })

export type RbqmExportFilters = {
  category?: TrendCategory
  period?: "month" | "quarter" | "6months" | "year" | "all"
}

/** Fetches the PPTX as a Blob and pops a browser download. Bypasses
 *  the JSON wrapper because the response is binary. */
export async function downloadRbqmTrendsPptx(filters: RbqmExportFilters = {}): Promise<string> {
  const token = await (await import("../auth/msalConfig")).acquireApiToken()
  const q = new URLSearchParams()
  if (filters.category) q.set("category", filters.category)
  if (filters.period) q.set("period", filters.period)
  const qs = q.toString()
  const url = `${API_BASE}/api/plugins/rbqm-trends/export${qs ? `?${qs}` : ""}`
  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  if (!r.ok) {
    let detail = ""
    try { detail = (await r.json()).detail || "" } catch { detail = await r.text() }
    throw new ApiError(r.status, detail)
  }
  const blob = await r.blob()
  // Extract filename from Content-Disposition if present, fall back to default.
  const cd = r.headers.get("Content-Disposition") || ""
  const m = /filename="?([^";]+)"?/i.exec(cd)
  const filename = m ? m[1] : `rbqm-trends-${new Date().toISOString().slice(0, 10)}.pptx`
  // Trigger the download via an anchor — works in all evergreen browsers.
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  return filename
}

export const pollRbqmSource = (id: string) =>
  api<RbqmPollResult>(`/api/plugins/rbqm-trends/sources/${encodeURIComponent(id)}/poll`, { method: "POST" })

export const pollAllRbqmSources = () =>
  api<RbqmPollAllResult>("/api/plugins/rbqm-trends/poll-all", { method: "POST" })

export type RbqmSourcePatch = {
  name?: string
  home_url?: string | null
  rss_url?: string | null
  enabled?: boolean
  notes?: string | null
}
export const patchRbqmSource = (id: string, patch: RbqmSourcePatch) =>
  api<RbqmSource>(`/api/plugins/rbqm-trends/sources/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  })

// --- Plugin: audit-explorer --------------------------------------------------
export type AuditEvent = {
  id: string
  actor: string
  action: string
  target_type: string
  target_id: string
  created_at: string
  trace_id: string | null
}

export type AuditPage = {
  events: AuditEvent[]
  next_offset: number | null
  total: number
}

export type AuditExplorerFilters = {
  actor?: string
  action?: string
  target_type?: string
  since_hours?: number
  sort?: "created_at" | "actor" | "action"
  descending?: boolean
  limit?: number
  offset?: number
}

export const listAuditEvents = (f: AuditExplorerFilters = {}) => {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v))
  }
  const qs = q.toString()
  return api<AuditPage>(`/api/plugins/audit/events${qs ? `?${qs}` : ""}`)
}

export type AuditCountByKey = { key: string; count: number }
export type AuditExplorerSummary = {
  as_of: string
  total: number
  window_hours: number
  by_action: AuditCountByKey[]
  by_actor: AuditCountByKey[]
}
export const auditSummary = (window_hours = 24, top_n = 10) =>
  api<AuditExplorerSummary>(`/api/plugins/audit/summary?window_hours=${window_hours}&top_n=${top_n}`)
